import { useState, useEffect, useCallback, useMemo } from "react";
import {
  getProduct,
  updateProduct,
  listProducts,
  type Product,
  type DefaultOperationalCostLine,
  INTERNAL_COST_TYPES,
} from "../../../api/catalog";
import { Button } from "../../primitives/Button";

/** Item consommable (section Coûts internes) : même structure que bundle item (obligatoire, quantité, cumul des coûts). */
export type AllowedConsumableItem = {
  product_id: string;
  required?: boolean;
  quantity?: number;
  cumulate_operational_cost?: boolean;
  cumulate_process_cost?: boolean;
  cumulate_purchase_cost?: boolean;
};

type ProductInternalCostsTabProps = {
  productId: string;
  onProductUpdate?: (product: Product) => void;
};

const DEFAULT_LABELS = ["Coordination interne", "Frais administratifs", "Supervision", "Support", "Contrôle qualité"];

export function ProductInternalCostsTab({ productId, onProductUpdate }: ProductInternalCostsTabProps) {
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lines, setLines] = useState<DefaultOperationalCostLine[]>([]);
  const [allowedConsumables, setAllowedConsumables] = useState<AllowedConsumableItem[]>([]);
  const [productsForPicker, setProductsForPicker] = useState<Product[]>([]);
  const [consumableModalOpen, setConsumableModalOpen] = useState(false);
  const [consumableSearch, setConsumableSearch] = useState("");
  /** "list" = choix produit ; "form" = formulaire Obligatoire / Quantité / Cumuler coûts */
  const [consumableModalStep, setConsumableModalStep] = useState<"list" | "form">("list");
  const [consumableModalProductId, setConsumableModalProductId] = useState("");
  const [consumableEditIndex, setConsumableEditIndex] = useState<number | null>(null);
  const [consumableModalRequired, setConsumableModalRequired] = useState(true);
  const [modalQuantity, setModalQuantity] = useState(1);
  const [modalCumulateOperational, setModalCumulateOperational] = useState(true);
  const [modalCumulateProcess, setModalCumulateProcess] = useState(true);
  const [modalCumulatePurchase, setModalCumulatePurchase] = useState(true);

  // Modal "Coûts internes par défaut" : ajout / édition d'une ligne
  const [costLineModalOpen, setCostLineModalOpen] = useState(false);
  const [editingLineIndex, setEditingLineIndex] = useState<number | null>(null);
  const [modalLabel, setModalLabel] = useState("");
  const [modalCostType, setModalCostType] = useState<"internal_labor" | "operational_fixed">("operational_fixed");
  const [modalAmount, setModalAmount] = useState<number>(0);
  const [modalHours, setModalHours] = useState<number>(0);
  const [modalRate, setModalRate] = useState<number>(0);
  const [modalAllowOverride, setModalAllowOverride] = useState(false);
  const [modalRequired, setModalRequired] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const p = await getProduct(productId);
      setProduct(p);
      const profile = p.operational_cost_profile ?? {};
      // Nouvelle structure
      const defaultCosts = profile.default_operational_costs;
      if (Array.isArray(defaultCosts) && defaultCosts.length > 0) {
        setLines(
          defaultCosts.map((row) => ({
            label: row.label ?? "",
            cost_type: row.cost_type ?? "operational_fixed",
            amount: row.amount,
            default_hours: row.default_hours,
            default_rate: row.default_rate,
            allow_override: row.allow_override ?? false,
            required: (row as { required?: boolean }).required ?? true,
          }))
        );
      } else {
        // Migration depuis internal_activities (ancien format) ou internal_fixed_cost (déplacé de Général → Phase 6)
        const legacy = profile.internal_activities ?? [];
        if (Array.isArray(legacy) && legacy.length > 0) {
          setLines(
            legacy.map((a) => ({
              label: (a as { activity_type?: string }).activity_type ?? "",
              cost_type: (a.cost_type === "internal_labor" ? "internal_labor" : "operational_fixed") as "internal_labor" | "operational_fixed",
              amount: a.cost_type === "operational_fixed" ? Number(a.cost) || 0 : undefined,
              default_hours: a.cost_type === "internal_labor" ? 1 : undefined,
              default_rate: a.cost_type === "internal_labor" ? Number(a.cost) || 0 : undefined,
              allow_override: true,
              required: true,
            }))
          );
        } else {
          // Migration internal_fixed_cost → première ligne du profil (cadrage Phase 6)
          const fixedCost = (p as { internal_fixed_cost?: number | null }).internal_fixed_cost;
          if (fixedCost != null && Number(fixedCost) > 0) {
            setLines([
              {
                label: "Coût fixe livraison/vente",
                cost_type: "operational_fixed",
                amount: Number(fixedCost),
                allow_override: false,
                required: true,
              },
            ]);
          } else {
            setLines([]);
          }
        }
      }
      const allowed = profile.allowed_consumables;
      if (Array.isArray(allowed) && allowed.length > 0) {
        setAllowedConsumables(
          allowed.map((c: Record<string, unknown>) => ({
            product_id: String(c.product_id),
            required: c.required !== false,
            quantity: typeof c.quantity === "number" && c.quantity >= 0.01 ? c.quantity : 1,
            cumulate_operational_cost: c.cumulate_operational_cost !== false,
            cumulate_process_cost: c.cumulate_process_cost !== false,
            cumulate_purchase_cost: c.cumulate_purchase_cost !== false,
          }))
        );
      } else {
        const ids = Array.isArray(profile.allowed_consumable_product_ids) ? profile.allowed_consumable_product_ids : [];
        setAllowedConsumables(
          ids.map((product_id: string) => ({
            product_id,
            required: false,
            quantity: 1,
            cumulate_operational_cost: true,
            cumulate_process_cost: true,
            cumulate_purchase_cost: true,
          }))
        );
      }
    } catch (e) {
      setError((e as Error)?.message ?? "Impossible de charger le produit.");
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let mounted = true;
    const PAGE_SIZE = 1000;
    const loadAll = async () => {
      const first = await listProducts({ page: 1, page_size: PAGE_SIZE, list: true });
      const all: Product[] = [...(first.items ?? [])];
      const total = first.meta?.total ?? 0;
      let page = 2;
      while (all.length < total && page <= Math.ceil(total / PAGE_SIZE)) {
        const res = await listProducts({ page, page_size: PAGE_SIZE, list: true });
        all.push(...(res.items ?? []));
        page += 1;
      }
      return all.slice(0, total);
    };
    loadAll()
      .then((all) => {
        if (mounted) setProductsForPicker(all);
      })
      .catch(() => {
        if (mounted) setProductsForPicker([]);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const totalDefaultCost = lines.reduce((sum, line) => {
    if (line.cost_type === "operational_fixed") return sum + (line.amount ?? 0);
    return sum + (line.default_hours ?? 0) * (line.default_rate ?? 0);
  }, 0);

  const productNameById = useMemo(
    () => Object.fromEntries(productsForPicker.map((p) => [p.id, p.name ?? p.code ?? p.id])),
    [productsForPicker]
  );
  const productCodeById = useMemo(
    () => Object.fromEntries(productsForPicker.map((p) => [p.id, p.code ?? ""])),
    [productsForPicker]
  );
  const consumableOptions = productsForPicker.filter(
    (p) => p.id !== productId && !allowedConsumables.some((c) => c.product_id === p.id)
  );
  const consumableModalFiltered = useMemo(() => {
    const term = consumableSearch.trim().toLowerCase();
    if (!term) return consumableOptions;
    return consumableOptions.filter(
      (p) =>
        (p.name ?? "").toLowerCase().includes(term) ||
        (p.code ?? "").toLowerCase().includes(term)
    );
  }, [consumableOptions, consumableSearch]);

  const openCostLineModalForAdd = () => {
    setEditingLineIndex(null);
    setModalLabel("");
    setModalCostType("operational_fixed");
    setModalAmount(0);
    setModalHours(0);
    setModalRate(0);
    setModalAllowOverride(false);
    setModalRequired(true);
    setCostLineModalOpen(true);
  };

  const openCostLineModalForEdit = (index: number) => {
    const line = lines[index];
    if (!line) return;
    setEditingLineIndex(index);
    setModalLabel(line.label);
    setModalCostType(line.cost_type);
    setModalAmount(line.cost_type === "operational_fixed" ? (line.amount ?? 0) : 0);
    setModalHours(line.cost_type === "internal_labor" ? (line.default_hours ?? 0) : 0);
    setModalRate(line.cost_type === "internal_labor" ? (line.default_rate ?? 0) : 0);
    setModalAllowOverride(line.allow_override);
    setModalRequired(line.required ?? true);
    setCostLineModalOpen(true);
  };

  const closeCostLineModal = () => {
    setCostLineModalOpen(false);
    setEditingLineIndex(null);
  };

  const submitCostLineModal = () => {
    const label = (modalLabel.trim() || (DEFAULT_LABELS[0] ?? "Nouvelle ligne")).trim();
    if (!label) return;
    const line: DefaultOperationalCostLine = {
      label,
      cost_type: modalCostType,
      allow_override: modalAllowOverride,
      required: modalRequired,
    };
    if (modalCostType === "operational_fixed") {
      line.amount = modalAmount;
    } else {
      line.default_hours = modalHours;
      line.default_rate = modalRate;
    }
    if (editingLineIndex !== null) {
      updateLine(editingLineIndex, line);
    } else {
      setLines((prev) => [...prev, line]);
    }
    closeCostLineModal();
  };

  const updateLine = (index: number, patch: Partial<DefaultOperationalCostLine>) => {
    setLines((prev) => {
      const next = [...prev];
      if (index >= 0 && index < next.length) {
        next[index] = { ...next[index], ...patch };
      }
      return next;
    });
  };

  const removeLine = (index: number) => {
    setLines((prev) => prev.filter((_, i) => i !== index));
  };

  const removeConsumable = (index: number) => {
    setAllowedConsumables((prev) => prev.filter((_, i) => i !== index));
  };

  const openConsumableModalForAdd = () => {
    setConsumableSearch("");
    setConsumableModalStep("list");
    setConsumableModalProductId("");
    setConsumableEditIndex(null);
    setConsumableModalRequired(true);
    setModalQuantity(1);
    setModalCumulateOperational(true);
    setModalCumulateProcess(true);
    setModalCumulatePurchase(true);
    setConsumableModalOpen(true);
  };

  const openConsumableModalForEdit = (index: number) => {
    const item = allowedConsumables[index];
    if (!item) return;
    setConsumableEditIndex(index);
    setConsumableModalProductId(item.product_id);
    setConsumableModalRequired(item.required !== false);
    setModalQuantity(Math.max(1, Math.round(typeof item.quantity === "number" ? item.quantity : 1)));
    setModalCumulateOperational(item.cumulate_operational_cost !== false);
    setModalCumulateProcess(item.cumulate_process_cost !== false);
    setModalCumulatePurchase(item.cumulate_purchase_cost !== false);
    setConsumableModalStep("form");
    setConsumableModalOpen(true);
  };

  const closeConsumableModal = () => {
    setConsumableModalOpen(false);
    setConsumableModalStep("list");
    setConsumableModalProductId("");
    setConsumableEditIndex(null);
  };

  const submitConsumableModal = () => {
    if (!consumableModalProductId) return;
    const quantity = Math.max(1, Math.round(Number(modalQuantity) || 1));
    const newItem: AllowedConsumableItem = {
      product_id: consumableModalProductId,
      required: consumableModalRequired,
      quantity,
      cumulate_operational_cost: modalCumulateOperational,
      cumulate_process_cost: modalCumulateProcess,
      cumulate_purchase_cost: modalCumulatePurchase,
    };
    if (consumableEditIndex !== null) {
      setAllowedConsumables((prev) => {
        const next = [...prev];
        next[consumableEditIndex] = newItem;
        return next;
      });
    } else {
      setAllowedConsumables((prev) => [...prev, newItem]);
    }
    closeConsumableModal();
  };

  const handleSave = async () => {
    if (!product) return;
    const valid = lines.every((l) => {
      if (!l.label.trim()) return false;
      if (l.cost_type === "operational_fixed") return (l.amount ?? 0) >= 0;
      return (l.default_hours ?? 0) >= 0 && (l.default_rate ?? 0) >= 0;
    });
    if (!valid) {
      setError("Chaque ligne doit avoir un libellé et des valeurs valides (≥ 0).");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const profile = product.operational_cost_profile ?? {};
      const default_operational_costs: DefaultOperationalCostLine[] = lines.map((l) => ({
        label: l.label.trim(),
        cost_type: l.cost_type,
        amount: l.cost_type === "operational_fixed" ? (l.amount ?? 0) : undefined,
        default_hours: l.cost_type === "internal_labor" ? (l.default_hours ?? 0) : undefined,
        default_rate: l.cost_type === "internal_labor" ? (l.default_rate ?? 0) : undefined,
        allow_override: l.allow_override,
        required: l.required ?? true,
      }));
      const updated = await updateProduct(productId, {
        internal_fixed_cost: null,
        operational_cost_profile: {
          ...profile,
          auto_generate_fixed_at_start: (profile as { auto_generate_fixed_at_start?: boolean }).auto_generate_fixed_at_start,
          allow_process_override: allowedConsumables.length > 0,
          default_operational_costs,
          allowed_consumables:
            allowedConsumables.length > 0
              ? allowedConsumables.map((c) => ({
                  product_id: c.product_id,
                  required: c.required !== false,
                  quantity: typeof c.quantity === "number" && c.quantity >= 0.01 ? c.quantity : 1,
                  cumulate_operational_cost: c.cumulate_operational_cost !== false,
                  cumulate_process_cost: c.cumulate_process_cost !== false,
                  cumulate_purchase_cost: c.cumulate_purchase_cost !== false,
                }))
              : undefined,
        },
      });
      setProduct(updated);
      onProductUpdate?.(updated);
    } catch (e) {
      setError((e as Error)?.message ?? "Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };


  if (loading) return <p className="text-sm text-muted">Chargement...</p>;
  if (error && !product) {
    return (
      <div className="rounded-md border border-danger/30 bg-danger/5 px-3 py-2 text-sm text-danger">
        {error}
      </div>
    );
  }

  const profile = product?.operational_cost_profile ?? {};
  const autoGenerateFixedAtStart = !!(profile as { auto_generate_fixed_at_start?: boolean }).auto_generate_fixed_at_start;

  const setAutoGenerateFixedAtStart = async (value: boolean) => {
    if (!productId || !product) return;
    try {
      const updated = await updateProduct(productId, {
        operational_cost_profile: { ...profile, auto_generate_fixed_at_start: value },
      });
      setProduct(updated);
      onProductUpdate?.(updated);
    } catch {
      // ignore
    }
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted">
        Définit les coûts internes générés lors de l&apos;exécution de ce produit. Ces coûts sont ajoutés automatiquement lors de la création d&apos;un dossier d&apos;exécution.
      </p>
      <p className="text-xs text-muted">
        Les coûts fournisseur se configurent dans <strong>Approvisionnement</strong>.
      </p>

      <div className="rounded-lg border border-border bg-muted/10 p-3 text-sm text-muted">
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={autoGenerateFixedAtStart}
            onChange={(e) => setAutoGenerateFixedAtStart(e.target.checked)}
            className="mt-1 rounded border-border"
          />
          <span>Ces coûts sont ajoutés automatiquement lorsqu&apos;un dossier d&apos;exécution est créé.</span>
        </label>
      </div>

      {/* 1. Coûts générés automatiquement */}
      <div className="rounded-md border border-border bg-surface-muted/30 p-4 space-y-4">
        <h4 className="text-sm font-medium text-text border-b border-border pb-2">
          Coûts générés automatiquement
        </h4>
        <p className="text-xs text-muted mb-1">Types de coûts :</p>
        <ul className="text-xs text-muted list-disc list-inside space-y-0.5">
          <li>Coût fixe</li>
          <li>Main-d&apos;œuvre (heures × taux)</li>
          <li>Coût variable (ex. $/km, $/acre)</li>
        </ul>

        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Button label="Ajouter" intent="primary" size="sm" onClick={openCostLineModalForAdd} />
        </div>

        {lines.length === 0 ? (
          <p className="text-sm text-muted">Aucune ligne. Cliquez sur « Ajouter » pour en créer une.</p>
        ) : (
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left p-3 font-medium text-text">Libellé</th>
                  <th className="text-left p-3 font-medium text-text">Type de coût</th>
                  <th className="text-left p-3 font-medium text-text">Montant / calcul</th>
                  <th className="text-left p-3 font-medium text-text">Modifiable au dossier</th>
                  <th className="text-left p-3 font-medium text-text">Obligatoire / Optionnel</th>
                  <th className="text-left p-3 font-medium text-text w-28">Actions</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line, index) => (
                  <tr key={index} className="border-b border-border/50 hover:bg-muted/20">
                    <td className="p-3 text-text">{line.label || "—"}</td>
                    <td className="p-3 text-text">
                      {INTERNAL_COST_TYPES.find((t) => t.value === line.cost_type)?.label ?? line.cost_type}
                    </td>
                    <td className="p-3 text-text">
                      {line.cost_type === "operational_fixed"
                        ? `${line.amount ?? 0} $`
                        : `${line.default_hours ?? 0} h × ${line.default_rate ?? 0} $/h`}
                    </td>
                    <td className="p-3 text-text">{line.allow_override ? "Oui" : "Non"}</td>
                    <td className="p-3 text-text">{line.required !== false ? "Obligatoire" : "Optionnel"}</td>
                    <td className="p-3 flex gap-2">
                      <button
                        type="button"
                        onClick={() => openCostLineModalForEdit(index)}
                        className="text-sm text-accent hover:underline"
                      >
                        Modifier
                      </button>
                      <button
                        type="button"
                        onClick={() => removeLine(index)}
                        className="text-sm text-danger hover:underline"
                      >
                        Supprimer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {lines.length > 0 && (
          <div className="pt-2 border-t border-border">
            <p className="text-sm font-medium text-text">
              Total coûts par défaut : <span className="text-primary">{totalDefaultCost.toFixed(2)} $</span>
            </p>
          </div>
        )}
      </div>

      {/* Modal Ajouter / Modifier une ligne de coût interne */}
      {costLineModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={closeCostLineModal}
        >
          <div
            className="rounded-lg border border-border bg-surface shadow-lg w-full max-w-md flex flex-col max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-border">
              <h4 className="text-sm font-semibold text-text">
                {editingLineIndex !== null ? "Modifier le coût interne" : "Ajouter un coût interne"}
              </h4>
            </div>
            <div className="p-4 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-xs font-medium text-text mb-1">Type de coût (libellé) *</label>
                <select
                  value={[...DEFAULT_LABELS, ...lines.map((l) => l.label.trim()).filter((l) => l).filter((l, i, a) => a.indexOf(l) === i)].includes(modalLabel) ? modalLabel : ""}
                  onChange={(e) => e.target.value && setModalLabel(e.target.value)}
                  className="w-full h-10 rounded-md border border-border bg-surface px-3 text-sm text-text"
                >
                  <option value="">— Choisir ou saisir ci-dessous —</option>
                  {DEFAULT_LABELS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                  {lines
                    .map((l) => l.label.trim())
                    .filter((l) => l && !DEFAULT_LABELS.includes(l))
                    .filter((l, i, a) => a.indexOf(l) === i)
                    .map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                </select>
                <input
                  type="text"
                  value={modalLabel}
                  onChange={(e) => setModalLabel(e.target.value)}
                  placeholder="Ou saisir un nouveau type (ex. Supervision, Contrôle qualité)"
                  className="mt-1 w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text placeholder:text-muted"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-text mb-1">Type de coût *</label>
                <select
                  value={modalCostType}
                  onChange={(e) =>
                    setModalCostType(e.target.value as "internal_labor" | "operational_fixed")
                  }
                  className="w-full h-10 rounded-md border border-border bg-surface px-3 text-sm text-text"
                >
                  {INTERNAL_COST_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              {modalCostType === "operational_fixed" ? (
                <div>
                  <label className="block text-xs font-medium text-text mb-1">Montant pour les calculs ($) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={modalAmount}
                    onChange={(e) => setModalAmount(parseFloat(e.target.value) || 0)}
                    className="w-full h-10 rounded-md border border-border bg-surface px-3 text-sm text-text"
                  />
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-xs font-medium text-text mb-1">Heures par défaut *</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={modalHours}
                      onChange={(e) => setModalHours(parseFloat(e.target.value) || 0)}
                      className="w-full h-10 rounded-md border border-border bg-surface px-3 text-sm text-text"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-text mb-1">Taux ($/h) *</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={modalRate}
                      onChange={(e) => setModalRate(parseFloat(e.target.value) || 0)}
                      className="w-full h-10 rounded-md border border-border bg-surface px-3 text-sm text-text"
                    />
                  </div>
                </>
              )}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={modalAllowOverride}
                  onChange={(e) => setModalAllowOverride(e.target.checked)}
                  className="rounded border-border"
                />
                <span className="text-sm text-text">Peut être modifié au dossier</span>
              </label>
              <div>
                <label className="block text-xs font-medium text-text mb-1">Application</label>
                <select
                  value={modalRequired ? "required" : "optional"}
                  onChange={(e) => setModalRequired(e.target.value === "required")}
                  className="w-full h-10 rounded-md border border-border bg-surface px-3 text-sm text-text"
                >
                  <option value="required">Obligatoire</option>
                  <option value="optional">Optionnel</option>
                </select>
                <p className="text-xs text-muted mt-1">Obligatoire = toujours appliqué au dossier ; Optionnel = au besoin.</p>
              </div>
            </div>
            <div className="p-4 border-t border-border flex justify-end gap-2">
              <Button label="Annuler" intent="ghost" size="sm" onClick={closeCostLineModal} />
              <Button
                label={editingLineIndex !== null ? "Enregistrer" : "Ajouter"}
                intent="primary"
                size="sm"
                onClick={submitCostLineModal}
              />
            </div>
          </div>
        </div>
      )}

      {/* 2. Consommables (tout type de produit-service, comme contenu bundle) */}
      <div className="rounded-md border border-border bg-surface-muted/30 p-4 space-y-4">
        <h4 className="text-sm font-medium text-text border-b border-border pb-2">
          Consommables
        </h4>
        <p className="text-xs text-muted">
          Produits ou services pouvant être consommés lors de l&apos;exécution. Vous pouvez sélectionner tout type de produit-service. Ex : pièces, batteries, crédits, services, etc.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            label="Ajouter"
            intent="primary"
            size="sm"
            onClick={openConsumableModalForAdd}
            disabled={consumableOptions.length === 0}
          />
          {consumableOptions.length === 0 && productsForPicker.length > 1 && (
            <span className="text-xs text-muted">Tous les autres produits sont déjà ajoutés.</span>
          )}
        </div>
        {allowedConsumables.length > 0 ? (
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left p-3 font-medium text-text">Produit / service</th>
                  <th className="text-center p-3 font-medium text-text w-24">Obligatoire</th>
                  <th className="text-center p-3 font-medium text-text w-20">Quantité</th>
                  <th className="text-center p-3 font-medium text-text w-24">Coût opér.</th>
                  <th className="text-center p-3 font-medium text-text w-24">Coût processus</th>
                  <th className="text-center p-3 font-medium text-text w-24">Coût d&apos;achat</th>
                  <th className="text-left p-3 font-medium text-text w-28">Actions</th>
                </tr>
              </thead>
              <tbody>
                {allowedConsumables.map((c, index) => (
                  <tr key={`${c.product_id}-${index}`} className="border-b border-border/50 hover:bg-muted/20">
                    <td className="p-3 text-text">
                      <div>
                        <span className="font-medium">{productNameById[c.product_id] ?? c.product_id}</span>
                        {productCodeById[c.product_id] && (
                          <span className="text-muted text-xs block mt-0.5">SKU : {productCodeById[c.product_id]}</span>
                        )}
                      </div>
                    </td>
                    <td className="p-3 text-center">{c.required !== false ? "Obligatoire" : "Optionnel"}</td>
                    <td className="p-3 text-center font-medium">
                      {typeof c.quantity === "number" && c.quantity >= 0.01 ? c.quantity : 1}
                    </td>
                    <td className="p-3 text-center">{c.cumulate_operational_cost !== false ? "Oui" : "Non"}</td>
                    <td className="p-3 text-center">{c.cumulate_process_cost !== false ? "Oui" : "Non"}</td>
                    <td className="p-3 text-center">{c.cumulate_purchase_cost !== false ? "Oui" : "Non"}</td>
                    <td className="p-3 flex gap-2">
                      <button
                        type="button"
                        onClick={() => openConsumableModalForEdit(index)}
                        className="text-sm text-accent hover:underline"
                      >
                        Modifier
                      </button>
                      <button
                        type="button"
                        onClick={() => removeConsumable(index)}
                        className="text-sm text-danger hover:underline"
                      >
                        Retirer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-muted">Aucun consommable. Cliquez sur « Ajouter » pour en ajouter.</p>
        )}
      </div>

      {/* Modale ajout/édition consommable : liste filtrable puis formulaire (comme bundle) */}
      {consumableModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={closeConsumableModal}
        >
          <div
            className="rounded-lg border border-border bg-surface shadow-lg w-full max-w-md flex flex-col max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-border">
              <h4 className="text-sm font-semibold text-text">
                {consumableEditIndex !== null
                  ? "Modifier l'item"
                  : consumableModalStep === "form"
                    ? "Ajouter un consommable"
                    : "Sélectionner un produit ou service"}
              </h4>
              {consumableModalStep === "list" && !consumableModalProductId && (
                <p className="text-xs text-muted mt-1">Recherchez puis sélectionnez un produit ou service dans la liste (tout type).</p>
              )}
            </div>

            {consumableModalStep === "list" && !consumableModalProductId ? (
              <>
                <div className="p-2 border-b border-border">
                  <input
                    type="text"
                    value={consumableSearch}
                    onChange={(e) => setConsumableSearch(e.target.value)}
                    placeholder="Rechercher par nom ou code…"
                    className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text placeholder:text-muted"
                    autoFocus
                  />
                </div>
                <div className="flex-1 overflow-y-auto min-h-0 p-2">
                  {consumableModalFiltered.length === 0 ? (
                    <p className="text-sm text-muted p-4 text-center">
                      {consumableSearch.trim() ? "Aucun produit ne correspond à la recherche." : "Aucun autre produit à ajouter."}
                    </p>
                  ) : (
                    <ul className="space-y-0.5">
                      {consumableModalFiltered.map((p) => (
                        <li key={p.id}>
                          <button
                            type="button"
                            onClick={() => {
                              setConsumableModalProductId(p.id);
                              setConsumableModalStep("form");
                              setConsumableModalRequired(true);
                              setModalQuantity(1);
                              setModalCumulateOperational(true);
                              setModalCumulateProcess(true);
                              setModalCumulatePurchase(true);
                            }}
                            className="w-full text-left rounded-md px-3 py-2 hover:bg-muted/30 text-sm text-text flex items-center gap-2"
                          >
                            {p.code && <span className="text-muted font-mono text-xs">{p.code}</span>}
                            <span className="truncate">{p.name ?? p.code ?? p.id}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            ) : (
              <div className="p-4 space-y-4 overflow-y-auto flex-1">
                <div className="flex items-center justify-between gap-2 rounded-md bg-muted/30 px-3 py-2">
                  <span className="text-sm text-text">
                    Produit sélectionné : <strong>{productNameById[consumableModalProductId] ?? consumableModalProductId}</strong>
                  </span>
                  {consumableEditIndex === null && (
                    <button
                      type="button"
                      onClick={() => {
                        setConsumableModalProductId("");
                        setConsumableModalStep("list");
                      }}
                      className="text-xs text-accent hover:underline"
                    >
                      Changer
                    </button>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-text mb-1">Dans le bundle</label>
                  <select
                    value={consumableModalRequired ? "required" : "optional"}
                    onChange={(e) => setConsumableModalRequired(e.target.value === "required")}
                    className="w-full h-10 rounded-md border border-border bg-surface px-3 text-sm text-text"
                  >
                    <option value="required">Obligatoire</option>
                    <option value="optional">Optionnel</option>
                  </select>
                  <p className="text-xs text-muted mt-1">Obligatoire = toujours inclus ; Optionnel = choisi par le client à la soumission.</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-text mb-1">Quantité</label>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={modalQuantity}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      setModalQuantity(Number.isNaN(v) || v < 1 ? 1 : v);
                    }}
                    className="w-full h-10 rounded-md border border-border bg-surface px-3 text-sm text-text"
                  />
                  <p className="text-xs text-muted mt-1">Nombre d&apos;unités de cet item. Impacte le calcul des coûts (fixe ou par unité).</p>
                </div>
                <div className="space-y-2">
                  <label className="block text-xs font-medium text-text">Cumuler les coûts de cet item lorsqu&apos;il est sélectionné</label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={modalCumulateOperational}
                      onChange={(e) => setModalCumulateOperational(e.target.checked)}
                      className="rounded border-border"
                    />
                    <span className="text-sm text-text">Coût opérationnel (coûts internes par défaut de l&apos;item)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={modalCumulateProcess}
                      onChange={(e) => setModalCumulateProcess(e.target.checked)}
                      className="rounded border-border"
                    />
                    <span className="text-sm text-text">Coût lié au processus (coûts ajoutés via le processus de l&apos;item)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={modalCumulatePurchase}
                      onChange={(e) => setModalCumulatePurchase(e.target.checked)}
                      className="rounded border-border"
                    />
                    <span className="text-sm text-text">Coût d&apos;achat (fournisseur)</span>
                  </label>
                </div>
              </div>
            )}

            <div className="p-4 border-t border-border flex justify-end gap-2">
              <Button label="Annuler" intent="ghost" size="sm" onClick={closeConsumableModal} />
              {consumableModalStep === "form" && consumableModalProductId && (
                <Button
                  label={consumableEditIndex !== null ? "Enregistrer" : "Ajouter"}
                  intent="primary"
                  size="sm"
                  onClick={submitConsumableModal}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {error && <p className="text-sm text-danger">{error}</p>}
      <Button
        label={saving ? "Enregistrement…" : "Enregistrer"}
        intent="primary"
        onClick={handleSave}
        disabled={saving}
        state={saving ? "loading" : "default"}
      />
    </div>
  );
}
